import { useTradingHub } from '@ovComponents/5-page/portfolioTradingHub/tradingHubContext';
import {
  BUCKET_SUB_TRADE_REQUESTS,
  CREATE_SUB_TRADE_REQUESTS,
} from '@ovComponents/5-page/portfolioTradingHub/utils/mutations';
import { useSnackbar } from 'hephaestus/web';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTradingMutation } from '../useTradingMutation';
import type { StagedTrade, StagedTradeGroup, TradeSource } from './types';

export const groupStagedTradesBySource = (
  trades: StagedTrade[],
): StagedTradeGroup[] => {
  const tradesBySource = new Map<string, StagedTradeGroup>();

  for (const trade of trades) {
    if (!trade.financialProductId) continue;
    const isGoal = trade.modelPortfolioType === 'GOAL';
    const source = isGoal ? 'GOAL' : 'SUB_ACCOUNT';
    const sourceId = isGoal
      ? trade.objectId || trade.subAccountId
      : trade.subAccountId;
    const key = `${source}:${sourceId}`;
    const group = tradesBySource.get(key) ?? {
      source,
      sourceId,
      trades: [],
    };
    group.trades.push(trade);
    tradesBySource.set(key, group);
  }

  return Array.from(tradesBySource.values());
};

export const mapStagedTradeToSubTradeRequest = (trade: StagedTrade) => ({
  subAccountId: trade.subAccountId,
  financialProductId: trade.financialProductId,
  portfolioReportId: trade.portfolioReportId,
  type: trade.side,
  moneyAllocatedCents: trade.amountCents,
  currency: trade.currency,
});

export const useCreateRebalanceTrades = ({
  organizationId,
  bulkTradingProcess,
  onTradesCreated,
}: {
  organizationId: string;
  bulkTradingProcess?: string;
  onTradesCreated: () => void;
}) => {
  const { setLastBulkCreationTime } = useTradingHub();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('portfolioTradingHub');
  const [creating, setCreating] = useState(false);
  const creatingRef = useRef(false);
  const [createSubTradeRequests] = useTradingMutation(
    CREATE_SUB_TRADE_REQUESTS,
  );
  const [bucketSubTradeRequests] = useTradingMutation(
    BUCKET_SUB_TRADE_REQUESTS,
  );

  const createTrades = useCallback(
    async (activeTrades: StagedTrade[]) => {
      if (creatingRef.current) {
        return { succeeded: 0, failed: 0 };
      }
      if (activeTrades.length === 0) {
        return { succeeded: 0, failed: 0 };
      }

      creatingRef.current = true;
      setCreating(true);
      try {
        const groupedTrades = groupStagedTradesBySource(activeTrades);
        if (groupedTrades.length === 0) {
          return { succeeded: 0, failed: 0 };
        }

        const successfulGroups: TradeSource[] = [];
        const failedGroups: TradeSource[] = [];

        for (const { source, sourceId, trades } of groupedTrades) {
          const subTradeRequests = trades.map(mapStagedTradeToSubTradeRequest);
          try {
            const result = await createSubTradeRequests({
              variables: { input: { source, sourceId, subTradeRequests } },
            });
            if (!result?.errors) {
              successfulGroups.push({ source, sourceId });
            } else {
              failedGroups.push({ source, sourceId });
            }
          } catch {
            failedGroups.push({ source, sourceId });
          }
        }

        if (successfulGroups.length === 0) {
          return { succeeded: 0, failed: failedGroups.length };
        }

        if (failedGroups.length > 0) {
          enqueueSnackbar(
            t('portfoliosTable.tradeToasts.createPartialSuccess', {
              succeeded: successfulGroups.length,
              total: successfulGroups.length + failedGroups.length,
              failed: failedGroups.length,
            }),
            { variant: 'warning' },
          );
        }

        if (bulkTradingProcess === 'BUCKET') {
          for (const { source, sourceId } of successfulGroups) {
            try {
              await bucketSubTradeRequests({
                variables: {
                  input: {
                    source,
                    sourceId,
                    ...(organizationId && { organizationId }),
                  },
                },
              });
            } catch {}
          }
        }

        setLastBulkCreationTime(Date.now());
        onTradesCreated();

        return {
          succeeded: successfulGroups.length,
          failed: failedGroups.length,
        };
      } finally {
        creatingRef.current = false;
        setCreating(false);
      }
    },
    [
      bulkTradingProcess,
      bucketSubTradeRequests,
      createSubTradeRequests,
      onTradesCreated,
      organizationId,
      setLastBulkCreationTime,
      enqueueSnackbar,
      t,
    ],
  );

  return { createTrades, creating };
};
